import moment from 'moment';

import { applicableCarriers, DISRUPTION_DELAY_THRESHOLD_MINUTES } from 'constants/timetableServices';
import { EUROSTAR_CARRIERS } from 'constants/serviceTypeCodes';
import { serviceNewsStatuses } from 'types/timetableServices';
import { isValidTimetableServicesQuery } from 'utils/timetableServices';

const DATE_FORMAT = 'YYYY-MM-DD';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const toTravelDate = (value) => {
  if (!value) {
    return undefined;
  }

  if (ISO_DATE.test(value)) {
    return value;
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? undefined : moment(parsed).format(DATE_FORMAT);
};

export const isTodayOrTomorrow = (date) => {
  if (!date) {
    return false;
  }

  const today = moment().format(DATE_FORMAT);
  const tomorrow = moment().add(1, 'day').format(DATE_FORMAT);

  return date === today || date === tomorrow;
};

export const isDepartureUpcoming = (leg) => {
  const { departureDate, departs } = leg?.timing ?? {};

  if (!departureDate || !departs) {
    return false;
  }

  return moment(`${departureDate}T${departs}`).isAfter(moment());
};

export const shouldQueryDisruptions = ({ date, originUic, destinationUic }) =>
  !!date && !!originUic && !!destinationUic && isTodayOrTomorrow(date) && isValidTimetableServicesQuery({ originUic, destinationUic });

const findDepartureStop = (service, originUic) => service.serviceStops?.find(({ station }) => station?.uic === originUic)?.model ?? {};

const delayFromNews = (service) => {
  const delays = (service.news ?? [])
    .filter(({ snRelation }) => snRelation?.status === serviceNewsStatuses.DELAYED)
    .map(({ snRelation }) => snRelation.delay)
    .filter(Number.isFinite);

  return Math.max(0, ...delays);
};

const delayFromTimetable = (stop) => {
  const scheduledAt = stop.commercialDepartureDateTime ?? stop.scheduledDepartureDateTime;

  if (!stop.expectedDepartureDateTime || !scheduledAt) {
    return 0;
  }

  const expected = moment(stop.expectedDepartureDateTime);
  const scheduled = moment(scheduledAt);

  if (!expected.isValid() || !scheduled.isValid()) {
    return 0;
  }

  return Math.max(0, expected.diff(scheduled, 'minutes'));
};

const readDisruption = (service, originUic) => {
  const { trainNumber, carrier } = service?.model ?? {};

  if (!trainNumber || !applicableCarriers.includes(carrier)) {
    return null;
  }

  const stop = findDepartureStop(service, originUic);

  if (stop.isDeparted) {
    return null;
  }

  const news = (service.news ?? []).map(({ model }) => model).filter(Boolean);
  const delay = Math.max(delayFromNews(service), delayFromTimetable(stop));
  const delayMinutes = delay >= DISRUPTION_DELAY_THRESHOLD_MINUTES ? delay : null;

  if (!delayMinutes && news.length === 0) {
    return null;
  }

  return { trainNumber, delayMinutes, news };
};

export const buildTrainDisruptions = ({ timetableServices, originUic }) => {
  const disruptions = new Map();

  (timetableServices ?? []).forEach((service) => {
    const disruption = readDisruption(service, originUic);

    if (disruption) {
      disruptions.set(disruption.trainNumber, disruption);
    }
  });

  return disruptions;
};

export const getLegDisruption = ({ disruptions, leg }) => {
  if (!disruptions?.size) {
    return null;
  }

  const isEurostarLeg = EUROSTAR_CARRIERS.includes(leg?.serviceType?.code);

  if (!isEurostarLeg || !isTodayOrTomorrow(leg?.timing?.departureDate) || !isDepartureUpcoming(leg)) {
    return null;
  }

  return disruptions.get(leg.trainNumber) ?? null;
};
